import { useEffect, useCallback, useRef, useState } from 'react';
import { TokenManager } from '../services/apiClient';
import { api } from '../services/api';

export interface LiveTrace {
  id: string;
  traceId: string;
  agentId: string;
  agentName?: string;
  status: string;
  confidenceScore: number;
  timestamp: string;
  createdAt: string;
}

export interface MilestoneEvent {
  milestone: string;
  traceId?: string;
  confidenceScore?: number | null;
  status?: string | null;
  _ts: number;
}

interface UseTraceStreamOptions {
  /** Maximum number of in-memory live events to keep in the buffer */
  bufferSize?: number;
  /** Called whenever a new trace arrives */
  onTrace?: (trace: LiveTrace) => void;
  /** Called when a non-trace milestone fires (first_trace_received, …) */
  onMilestone?: (event: MilestoneEvent) => void;
  /** Whether the stream should be active */
  enabled?: boolean;
}

interface UseTraceStreamReturn {
  isConnected: boolean;
  isConnecting: boolean;
  liveTraces: LiveTrace[];
  clearLive: () => void;
}

const BASE_URL = (typeof window !== 'undefined' && (window as any).__VITE_API_URL__) || '';
const SSE_URL = `${BASE_URL}/api/traces/stream`;

export function useTraceStream({
  bufferSize = 50,
  onTrace,
  onMilestone,
  enabled = true,
}: UseTraceStreamOptions = {}): UseTraceStreamReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [liveTraces, setLiveTraces] = useState<LiveTrace[]>([]);

  const esRef = useRef<EventSource | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTraceRef = useRef(onTrace);
  onTraceRef.current = onTrace;
  const onMilestoneRef = useRef(onMilestone);
  onMilestoneRef.current = onMilestone;

  const clearLive = useCallback(() => setLiveTraces([]), []);

  const connect = useCallback(async () => {
    if (!enabled) return;

    // Require a JWT to be present before attempting stream token exchange
    if (!TokenManager.getAccessToken()) return;

    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    setIsConnecting(true);

    let streamToken: string;
    try {
      // Exchange JWT for a short-lived single-use stream token.
      // This avoids putting the full JWT in the SSE URL (which would appear in logs).
      const result = await api.getStreamToken();
      streamToken = result.streamToken;
    } catch {
      // If token exchange fails (e.g. session expired), retry after delay
      setIsConnecting(false);
      if (enabled) {
        clearTimeout(reconnectTimer.current ?? undefined);
        reconnectTimer.current = setTimeout(() => connect(), 5000);
      }
      return;
    }

    const url = `${SSE_URL}?stream_token=${encodeURIComponent(streamToken)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener('trace', (e: MessageEvent) => {
      try {
        const trace: LiveTrace = JSON.parse(e.data);
        onTraceRef.current?.(trace);
        setLiveTraces((prev) => {
          const next = [trace, ...prev];
          return next.length > bufferSize ? next.slice(0, bufferSize) : next;
        });
      } catch (err) {
        console.warn('[useTraceStream] Failed to parse SSE event:', e.data, err);
      }
    });

    es.addEventListener('milestone', (e: MessageEvent) => {
      try {
        const ev: MilestoneEvent = JSON.parse(e.data);
        onMilestoneRef.current?.(ev);
      } catch (err) {
        // Cardinal Rule #4: never log raw event body
        if (import.meta.env.DEV) {
          console.warn('[useTraceStream] Failed to parse milestone event', err);
        }
      }
    });

    es.addEventListener('open', () => {
      setIsConnected(true);
      setIsConnecting(false);
    });

    es.addEventListener('error', () => {
      setIsConnected(false);
      setIsConnecting(false);
      es.close();
      esRef.current = null;

      // Reconnect after 5 seconds — clear any pending timer first to avoid stacking
      if (enabled) {
        clearTimeout(reconnectTimer.current ?? undefined);
        reconnectTimer.current = setTimeout(() => {
          connect();
        }, 5000);
      }
    });
  }, [enabled, bufferSize]);

  useEffect(() => {
    if (!enabled) {
      esRef.current?.close();
      esRef.current = null;
      setIsConnected(false);
      setIsConnecting(false);
      return;
    }

    connect();

    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      esRef.current?.close();
      esRef.current = null;
    };
  }, [enabled, connect]);

  return { isConnected, isConnecting, liveTraces, clearLive };
}
